import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CreditCard, Loader2, ArrowLeft, FileDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Panel, Stat } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { BatchStatus } from "@/pages/Bulk";
import { FixFromReport } from "@/components/FixFromReport";
import { BatchTypoFix } from "@/components/BatchTypoFix";
import { BatchAddressFix } from "@/components/BatchAddressFix";
import { api, formatError, fmtDate, gbp } from "@/lib/api";

const PayBatch = ({ b }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bulk/batches/${b.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(formatError(e));
      setBusy(false);
    }
  };
  return (
    <div>
      <button data-testid="batch-pay-btn" onClick={pay} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Pay {gbp(b.total_gbp)} & send {b.count} letters
      </button>
      {err && <p className="text-sm text-red-600 mt-2">{err}</p>}
    </div>
  );
};

const ReportButton = ({ b, path = "report.csv", name = `PostalCloud-Batch-${b.reference}.csv`, label = "Download report (CSV)", testId = "batch-report-btn", danger }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/bulk/batches/${b.id}/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      Object.assign(document.createElement("a"), { href: url, download: name }).click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid={testId} onClick={download} disabled={busy} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-semibold transition-colors disabled:opacity-60 ${danger ? "border-red-300 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40" : "border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
      {busy ? <Loader2 size={15} className="animate-spin" /> : danger ? <AlertTriangle size={15} /> : <FileDown size={15} />} {label}
    </button>
  );
};

export default function BatchDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let timer;
    const load = () => api.get(`/bulk/batches/${id}`).then((r) => {
      setD(r.data);
      if (["processing", "sending"].includes(r.data.batch.status)) timer = setTimeout(load, 2500);
    }).catch((e) => setError(formatError(e)));
    load();
    return () => clearTimeout(timer);
  }, [id]);
  if (error) return <p data-testid="batch-error" className="text-red-600">{error}</p>;
  if (!d) return <Loader2 className="animate-spin text-emerald-600" />;
  const { batch: b, letters, counts } = d;
  return (
    <div data-testid="batch-detail-page" className="space-y-6">
      <Link to="/app/bulk" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white" data-testid="batch-back-link"><ArrowLeft size={14} /> All batches</Link>
      <PageHeader eyebrow={`${b.reference} · bulk batch`} title={b.name}>
        <div className="flex flex-wrap items-center gap-3">
          <BatchStatus status={b.status} testId="batch-status" />
          {b.status === "awaiting_payment" && <PayBatch b={b} />}
          {b.status !== "processing" && <ReportButton b={b} />}
          {d.problems > 0 && <ReportButton b={b} path="problems.csv" name={`PostalCloud-Batch-${b.reference}-undelivered.csv`} label={`Undelivered letters (${d.problems})`} testId="batch-problems-btn" danger />}
        </div>
      </PageHeader>
      {b.status === "processing" && <p data-testid="batch-processing" className="text-sm text-amber-700 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Securely storing letters: {b.processed} of {b.count}</p>}
      {b.error && <p className="text-sm text-red-600">{b.error}</p>}
      {b.phones_removed_rows?.length > 0 && <p data-testid="batch-phones-removed" className="text-sm text-slate-600 dark:text-slate-400">Sent without a mobile number (the number couldn't receive texts): CSV row{b.phones_removed_rows.length > 1 ? "s" : ""} {b.phones_removed_rows.join(", ")}. These recipients get their letter by email only.</p>}
      {b.status === "awaiting_payment" && b.address_warnings?.length > 0 && (
        <BatchAddressFix batch={b} letters={letters} onChange={() => api.get(`/bulk/batches/${id}`).then((r) => setD(r.data))} />
      )}
      {b.status === "awaiting_payment" && b.contact_warnings?.length > 0 && (
        <BatchTypoFix batch={b} letters={letters} onChange={() => api.get(`/bulk/batches/${id}`).then((r) => setD(r.data))} />
      )}
      {d.problems > 0 && <FixFromReport batchId={b.id} onDone={() => window.location.reload()} />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testId="batch-stat-count" label="Letters" value={b.count} />
        <Stat testId="batch-stat-total" label={`Total at ${gbp(b.unit_price_gbp)} each`} value={gbp(b.total_gbp)} accent />
        <Stat testId="batch-stat-pending" label="Awaiting claim" value={counts.pending_claim || 0} />
        <Stat testId="batch-stat-claimed" label="Claimed" value={counts.claimed || 0} />
      </div>
      <Panel title="Integrity">
        <p className="text-xs text-slate-500">CSV SHA-256 <span className="font-mono break-all">{b.csv_sha256}</span></p>
        <p className="text-xs text-slate-500 mt-1">ZIP SHA-256 <span className="font-mono break-all">{b.zip_sha256}</span></p>
      </Panel>
      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <table className="w-full text-sm" data-testid="batch-letters-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-800">
            {["Ref", "Recipient", "File", "Status", "Notified"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>{letters.map((m) => (
            <tr key={m.id} className="border-b last:border-0 border-slate-100 dark:border-slate-800">
              <td className="px-4 py-3"><Link to={`/app/mail/${m.id}`} className="font-mono text-xs underline">{m.reference}</Link></td>
              <td className="px-4 py-3 whitespace-nowrap">{m.recipient_name} <span className="text-slate-500">· {m.recipient_email}</span></td>
              <td className="px-4 py-3 text-xs">{m.file_name}</td>
              <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
              <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{fmtDate(m.notified_at)}</td>
            </tr>))}
          </tbody>
        </table>
        {!letters.length && <p className="text-sm text-slate-500 p-6 text-center">Letters appear here as they are stored.</p>}
      </div>
    </div>
  );
}
